/**
 * One-way-only mode (`data-prop-hide-round-trip`).
 *
 * The rule is enforced in layers so no single leak reaches the funnel: prefilled dates are
 * dropped at the source, search fields are sanitized before building URLs, and each picker
 * hides its return and open-ticket surfaces. With `hideRoundTrip` off every helper is a no-op.
 */

/**
 * Drops a prefilled return date and an open-ticket departure. The open-ticket text is
 * localized, so callers pass the resolved `openTicketText`.
 */
export const sanitizePrefilledDates = ({
  hideRoundTrip,
  departureDate,
  returnDate,
  openTicketText,
}) => {
  if (!hideRoundTrip) return { departureDate, returnDate };

  return {
    departureDate: departureDate === openTicketText ? '' : departureDate,
    returnDate: '',
  };
};

/**
 * Forces one-way semantics on the fields sent to the results URL and mobility leave-behind.
 */
export const sanitizeSearchFields = (fields, hideRoundTrip) => {
  if (!hideRoundTrip) return fields;

  return { ...fields, tripType: 'oneWay', hybridTrip: 'none', returnDate: null };
};

/**
 * Resolves the effective trip-section flags for the mounted picker.
 *
 * Payment-methods placement keeps its current inputs on the unified picker; on the legacy
 * picker it receives the effective flags, so with no trip section rendered in the top-wrapper
 * the payment methods leave it (see `computeHasPaymentMethodsInTop`).
 */
export const resolveTripVisibility = ({
  hideRoundTrip,
  showUnifiedPicker,
  showOpenTicket,
  hideReturn,
  calendarOpen,
  showDepartureOpenButton,
  showOpenTicketCTAOnReturn,
}) => {
  const effective = hideRoundTrip
    ? {
        showOpenTicket: false,
        hideReturn: true,
        showDepartureOpenButton: false,
        showOpenTicketCTAOnReturn: false,
      }
    : { showOpenTicket, hideReturn, showDepartureOpenButton, showOpenTicketCTAOnReturn };

  const paymentFlagsSource = showUnifiedPicker ? { showOpenTicket, hideReturn } : effective;

  return {
    ...effective,
    paymentMethodsInTopFlags: {
      showOpenTicket: paymentFlagsSource.showOpenTicket,
      calendarOpen,
      hideReturn: paymentFlagsSource.hideReturn,
    },
  };
};
